import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import configDetails from '../Config/Config';

const ManualTextModal = ({ show, onClose, onAddText, timer, getNewDivId, authIdToken }) => {
  const [text, setText] = useState("");
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const quillRef = React.useRef(null);

  // Custom image handler to restrict to one image only and set editor to read-only after image insert
  const imageHandler = () => {
    const quill = quillRef.current.getEditor();
    const delta = quill.getContents();
    // Check if there's already an image in the editor
    const hasImage = delta.ops.some(op => op.insert && op.insert.image);
    if (hasImage) {
      alert("Only one image is allowed. Please remove the existing image before adding a new one.");
      return;
    }
    // Create file input for image selection
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = () => {
      const file = input.files[0];
      if (file) {
        // Check file size (<= 3MB)
        const maxSize = 3 * 1024 * 1024; // 3MB in bytes
        if (file.size > maxSize) {
          alert("Image size should be 3MB or less. Please select a smaller image.");
          return;
        }
        setSelectedImageFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          // Clear all text before inserting image
          quill.setContents([]);
          quill.insertEmbed(0, 'image', e.target.result);
          // Set editor to read-only after image insert
          quill.enable(false);
        };
        reader.readAsDataURL(file);
      }
    };
  };
  // Effect to re-enable editor if image is removed
  useEffect(() => {
    if (!show) return;
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const checkImageAndToggle = () => {
      const delta = quill.getContents();
      const hasImage = delta.ops.some(op => op.insert && op.insert.image);
      if (!hasImage) {
        quill.enable(true);
      }
    };
    quill.on('text-change', checkImageAndToggle);
    return () => {
      quill.off('text-change', checkImageAndToggle);
    };
  }, [show]);

  // Function to check if image is present in editor
  const hasImageInEditor = () => {
    if (!quillRef.current) return false;
    const quill = quillRef.current.getEditor();
    const delta = quill.getContents();
    return delta.ops.some(op => op.insert && op.insert.image);
  };

  // Function to handle image upload flow
  const handleImageUpload = async () => {
    if (!selectedImageFile) return null;

    try {
      setIsUploading(true);
      const { trackingUrl, GET_FILE_URLS, UPLOAD_STATUS } = configDetails;
      const fileName = selectedImageFile.name.replace(/\s+/g, '_');

      // Gather metadata for payload
      // You may need to pass these from props/context if not available here
      const userId = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).userSub : '';
      const sessionId = localStorage.getItem('session_id') || '';
      const conversationId = localStorage.getItem('conversation_id') || '';
      const divId = getNewDivId(); 
      const sequenceId = 0;
      const createTimestamp = new Date().toISOString().replace('T', ' ').replace('Z', '');
      const duration = timer;
      const contentType = 'image';

      // Step 1: Trigger POST request to /presigned_information with metadata
      const response = await fetch(`${trackingUrl}${GET_FILE_URLS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authIdToken
        },
        body: JSON.stringify({
          file_name: fileName,
          user_id: userId,
          session_id: sessionId,
          conversation_id: conversationId,
          div_id: divId,
          sequence_id: sequenceId,
          create_timestamp: createTimestamp,
          duration: duration,
          content_type: contentType
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to get file URLs: ${response.statusText}`);
      }

      const data = await response.json();
      const { upload_url, download_url, s3_uri } = data;

      // Step 2: Upload image to S3 using upload_url
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': selectedImageFile.type
        },
        body: selectedImageFile
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload image to S3: ${uploadResponse.statusText}`);
      }

      // Step 3: Notify backend of upload status
      try {
        await fetch(`${trackingUrl}${UPLOAD_STATUS}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authIdToken
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            div_id: divId
          })
        });
      } catch (notifyErr) {
        // Log but do not block UI on this error
        console.error('Failed to notify upload status:', notifyErr);
      }

      // Remove the preview image from the editor since we'll display it in transcript
      const quill = quillRef.current.getEditor();
      const delta = quill.getContents();
      const newDelta = { ops: delta.ops.filter(op => !(op.insert && op.insert.image)) };
      quill.setContents(newDelta);
      setText(quill.root.innerHTML);

      return { upload_url, download_url, s3_uri };

    } catch (error) {
      console.error('Image upload failed:', error);
      alert(`Image upload failed: ${error.message}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Quill toolbar options for rich text and code
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      [{ 'font': [] }],
      [{ 'size': [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };
  const quillFormats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote', 'code-block',
    'list', 'bullet', 'align',
    'link', 'image'
  ];

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
      
      // Set up custom image handler after Quill is initialized
      setTimeout(() => {
        if (quillRef.current) {
          const quill = quillRef.current.getEditor();
          const toolbar = quill.getModule('toolbar');
          if (toolbar) {
            toolbar.addHandler('image', imageHandler);
          }
        }
      }, 100);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [show]);

  if (!show) return null;

  return (

  <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.25)', zIndex: 99999, pointerEvents: 'auto' }}>
        <div className="modal-dialog" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', padding: 0, minWidth: 350, maxWidth: 600, zIndex: 100000, pointerEvents: 'auto', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 32, overflowY: 'auto', flex: 1 }}>
          <h5 className="mb-3">Add Manual Text</h5>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ReactQuill
              ref={quillRef}
              className="mb-3"
              theme="snow"
              value={text}
              onChange={setText}
              modules={quillModules}
              formats={quillFormats}
              style={{ minHeight: 120, maxHeight: 300, background: '#fff', width: '100%' }}
            />
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2" style={{ padding: '16px 32px', borderTop: '1px solid #eee', background: '#fff' }}>
          <button
            className="btn btn-outline-secondary"
            onClick={() => {
              setSelectedImageFile(null);

              if (quillRef.current) {
                const quill = quillRef.current.getEditor();
                quill.setContents([]);
              }
              setText("");
              onClose();
            }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={(!text || text === '<p><br></p>') || isUploading}
            onClick={async () => {
              if (text && text !== '<p><br></p>') {                
                // Check if image is present and handle upload
                let imageData = null;
                if (hasImageInEditor() && selectedImageFile) {
                  const uploadResult = await handleImageUpload();
                  if (!uploadResult) {
                    // Upload failed, don't proceed
                    return;
                  }
                  imageData = uploadResult;
                }
                
                // Pass both text and image data to Record component
                onAddText(text, imageData);
                setText("");
                setSelectedImageFile(null);
                onClose();
              }
            }}
          >
            Add Text
          </button>
        </div>
        </div>
      </div>
  );
};

export default ManualTextModal;